<template>
  <!-- Listener pour l'ouverture du dialog de demande de devis -->
  <VBusHelper
    :reactOn="$constants.EVENTS.QUOTE.OPEN"
    :handler="handleOpen"
  />

  <DialogComponent ref="dialog" type="modal">
    <div class="space-y-6 max-w-lg mx-auto">
      <!-- Header -->
      <div class="text-center mb-6">
        <div class="inline-block p-4 rounded-full bg-primary-100 mb-4">
          <ColorImg
            src="/images/icons/graduation-cap.svg"
            class="bg-primary-600 w-12 h-12"
          />
        </div>
        <h3 class="text-2xl font-bold text-gray-800 mb-2">
          Recevoir un devis
        </h3>
        <p class="text-gray-600">
          Complétez ce formulaire et nous vous enverrons un devis personnalisé sous 24h.
        </p>
      </div>

      <!-- Formulaire -->
      <form v-if="!isSuccess" @submit.prevent="handleSubmit" class="space-y-5">
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
            <input
              v-model="form.fullName"
              type="text"
              required
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
              placeholder="Votre nom"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                v-model="form.email"
                type="email"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="votre@email.com"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input
                v-model="form.phone"
                type="tel"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="+XXX XX XX XX"
              />
            </div>
          </div>
          <div v-if="contextTitle" class="bg-gray-50 p-3 rounded-lg border border-gray-100">
            <p class="text-xs text-gray-500 uppercase font-semibold mb-1">Service concerné</p>
            <p class="text-sm font-bold text-gray-800">{{ contextTitle }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Précisions (optionnel)</label>
            <textarea
              v-model="form.message"
              rows="3"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all resize-none"
              placeholder="Nombre de participants, besoins spécifiques..."
            ></textarea>
          </div>
        </div>

        <div class="pt-4">
          <button
            type="submit"
            :disabled="isSending"
            class="w-full bg-primary-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-primary-700 transition-all disabled:opacity-50 shadow-lg shadow-primary-200 flex items-center justify-center gap-3"
          >
            <div v-if="isSending" class="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span v-else>Envoyer ma demande de devis</span>
          </button>
        </div>
      </form>

      <!-- Message de succès -->
      <div v-else class="py-12 text-center space-y-6">
        <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <ColorImg 
            src="/images/icons/check.svg" 
            class="bg-green-600 w-10 h-10" 
          />
        </div>
        <div>
          <h4 class="text-2xl font-bold text-gray-800 mb-2">Demande reçue !</h4>
          <p class="text-gray-600">
            Merci <strong>{{ form.fullName }}</strong>. Votre devis pour <strong>{{ contextTitle }}</strong> est en préparation. Vous le recevrez par email très prochainement.
          </p>
        </div>
        <button @click="closeDialog" class="px-8 py-3 bg-gray-800 text-white rounded-xl font-semibold hover:bg-black transition-all">
          Fermer
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, reactive, getCurrentInstance } from 'vue';
import { sendToTelegram } from '@/app/services/telegramService';

const { proxy } = getCurrentInstance();
const { $constants } = proxy;

const dialog = ref(null);
const isSending = ref(false);
const isSuccess = ref(false);
const contextTitle = ref('');

const form = reactive({
  fullName: '',
  email: '',
  phone: '',
  message: ''
});

const handleOpen = (payload) => {
  isSuccess.value = false;
  isSending.value = false;
  contextTitle.value = payload?.title || 'Certification / Formation';
  
  if (payload?.customerInfo) {
    form.fullName = payload.customerInfo.fullName || '';
    form.email = payload.customerInfo.email || '';
    form.phone = payload.customerInfo.phone || '';
  }

  dialog.value.show();
};

const handleSubmit = async () => {
  isSending.value = true;
  try {
    const success = await sendToTelegram('📝 DEMANDE DE DEVIS', {
      ...form,
      service: contextTitle.value,
      source: 'Formulaire "Recevoir un devis"'
    });

    if (success) {
      isSuccess.value = true;
    } else {
      alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
    }
  } catch (error) {
    console.error('Quote request error:', error);
    alert('Erreur technique.');
  } finally {
    isSending.value = false;
  }
};

const closeDialog = () => {
  dialog.value.hide();
  form.message = '';
};
</script>
